import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Sun,
    Sparkles,
    SmilePlus,
    Heart,
    Layers,
    Camera,
    Baby,
    Gift,
    Smile,
    Sliders,
    Clock,
    Image as ImageIcon
} from 'lucide-react';

const childServices = [
    { id: '01', icon: Sun, title: 'Outdoor Play & Adventure', desc: 'Capturing your child running free in sun-drenched meadows or local parks, prioritizing unscripted movement and raw childhood joy.' },
    { id: '02', icon: SmilePlus, title: 'Pure Expression Portraiture', desc: 'Ditching the forced "say cheese" smiles for genuine, soulful expressions that capture your child’s true, authentic personality.' },
    { id: '03', icon: Sparkles, title: 'Whimsical Fine-Art Themes', desc: 'Ethereal, high-concept studio styling that transforms everyday childhood imagination into timeless, storybook-inspired visual art.' },
    { id: '04', icon: Heart, title: 'Sibling Connection Sessions', desc: 'Documenting the unique, chaotic, and beautiful bonds between siblings through interactive games and shared laughter.' },
    { id: '05', icon: Baby, title: 'Toddler Milestone Markers', desc: 'Celebrating those massive developmental leaps—from tentative first steps to expressive toddler curiosity and discovery.' },
    { id: '06', icon: Gift, title: 'Fine-Art Birthday Portraits', desc: 'Elegant, stylized celebratory sessions designed to anchor their personality profile at each passing year of growth.' },
];

const childDeliverables = [
    { id: '01', icon: Clock, title: 'Patience-First Buffer Booking', desc: 'Extended session windows to completely accommodate snack breaks, spontaneous tantrums, and outfit transitions without any stress.' },
    { id: '02', icon: SmilePlus, title: 'Game-Driven Direction Frameworks', desc: 'Utilizing playful prompts, secret riddles, and interactive fun instead of clinical positions to evoke natural laughter.' },
    { id: '03', icon: Sliders, title: 'Ultra-Fast Kinetic Capture Metric', desc: 'Configuring lighting setups and camera configurations to track hyper-fast movements without losing focal clarity.' },
    { id: '04', icon: Sparkles, title: 'Organic Texture Tonal Finishing', desc: 'Flawless retouching that cleans up temporary scratches or playground bruises while fiercely preserving authentic freckles and rosy cheeks.' },
    { id: '05', icon: Layers, title: 'Curated Prop & Toy Integration', desc: 'Weaving your child\'s favorite security items, vintage toys, or seasonal textures seamlessly into artistic framings.' },
    { id: '06', icon: Sun, title: 'Natural Ambient Emulation Light', desc: 'Deploying highly diffused, eye-safe strobe systems that never startle, mimic gentle window light, and allow kids to roam freely.' },
    { id: '07', icon: Smile, title: 'Comfort-Centered Environment Flow', desc: 'Adapting layouts dynamically to accommodate shy or highly energetic temperaments, ensuring a safe and sensory-friendly set.' },
    { id: '08', icon: Heart, title: 'Family Integration Modules', desc: 'Seamlessly welcoming mom and dad into the frame for unposed, candid moments of pure warmth and interaction.' },
    { id: '09', icon: ImageIcon, title: 'Lifetime Redundant Visual Vaulting', desc: 'Archiving your family assets across redundant cloud systems to ensure your precious child milestone markers remain secure for decades.' },
];

const ChildPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.52;
        const speedRight = 0.32;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Child Photography Portfolio in Hyderabad | Kids Portfolio Photography | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional child photography portfolio in Hyderabad by KhultStudios. We create stunning kids' portfolios for child actors, baby models, fashion, casting auditions, advertisements, and talent agencies with creative indoor and outdoor photoshoots."
                />

                <meta
                    name="keywords"
                    content="Child Photography Portfolio Hyderabad, Kids Portfolio Photography, Child Actor Portfolio, Baby Model Portfolio, Kids Photoshoot Hyderabad, Child Model Photography, Kids Fashion Photography, Child Casting Portfolio, Kids Headshots Hyderabad, Baby Photography Portfolio, Child Portfolio Shoot, Kids Portfolio Photographer Hyderabad, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/child-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/child-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Child Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Professional child portfolio photography for child actors, baby models, casting auditions, fashion brands, and advertisements in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/child-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Child Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional kids' portfolio photography, child actor headshots, and baby model portfolio shoots in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/child-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/children_candid_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Fine-Art & Candid Child Storytellers
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Freezing the Magic of Childhood
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Children grow and change in the blink of an eye. They don't belong in frozen, clinical poses or forced studio alignments—their magic lives in spontaneous curiosity, breathless laughter, running barefoot through grass, and quiet moments of wonder.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we specialize in high-patience child portraiture. By utilizing game-driven prompt modules, rapid kinetic shutter syncing, and sensory-friendly environments, we document your child's raw, unforced personality with absolute clarity.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Milestones
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }} >
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=500" alt="Child Running Joyfully Through Golden Sunlit Field" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1484665754804-74b091211472?q=80&w=500" alt="Siblings Sharing Deep Hug and Genuine Laugh" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519689680058-324335c77eba?q=80&w=500" alt="Whimsical Fine Art Child Portrait in Studio" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=500" alt="Toddler Curious Expression Close Up" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?q=80&w=500" alt="Child Running Joyfully Through Golden Sunlit Field Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1484665754804-74b091211472?q=80&w=500" alt="Siblings Sharing Deep Hug and Genuine Laugh Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1519689680058-324335c77eba?q=80&w=500" alt="Whimsical Fine Art Child Portrait in Studio Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?q=80&w=500" alt="Toddler Curious Expression Close Up Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/child-photography/child-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* CHILD DEVELOPMENT PORTFOLIOS SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">✨ Specialized Childhood Milestone Sessions</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Preserving the unique chapters of childhood requires a relaxed pace. Our specialized, play-centric modules adapt completely to your child\'s energy levels to highlight their real charm.
                    </p>
                    <div className="row g-4 mt-2">
                        {childServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 1 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL EXECUTION & PROCESS SECTION */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Execution & Supportive Session Frameworks</h2>
                    <div className="row g-4">
                        {childDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ChildPhotographyPortfolio;